<script setup lang="ts">
/**
 * Featured Videos Section - Clean design with StandardSlider.
 * Videos are loaded lazily by VideoCard when visible.
 * On mobile, the active slide auto-plays (no hover).
 * Falls back to watched videos if all new ones have been watched.
 */
import type { FeaturedVideoProperty } from '~/core/entities/property';

import {
	breakpointsTailwind,
	useBreakpoints,
	useIntersectionObserver
} from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { useFeaturedVideosModal } from '~/composables/useFeaturedVideosModal';
import { useLastVideoState } from '~/composables/useLastVideoState';
import { MixpanelProps } from '~/core/service/mixpanel';
import { useFeaturedVideosStore } from '~/store/featuredVideosStore';

import { VideoCard } from '~/components/VideoCard';

import SectionSkeleton from './SectionSkeleton.vue';

// Track active slide for mobile auto-play
const activeSlideIndex = ref(0);

// Mobile detection - only on client to avoid hydration mismatch
const isMobile = ref(false);

// Section visibility for mobile autoplay
const sectionRef = ref<HTMLElement | null>(null);
const isSectionVisible = ref(false);

// Should autoplay on mobile: section is visible AND is mobile
const shouldAutoplayMobile = computed(
	() => isMobile.value && isSectionVisible.value
);

const {
	navigateToReels,
	historyItems,
	loadHistory,
	isHistoryLoaded,
	isLoadingHistory
} = useFeaturedVideosModal();
const store = useFeaturedVideosStore();

const isLoading = computed(
	() => store.isLoading || !store.isPreloaded || isLoadingHistory.value
);

// Combine new videos with history (deduplicated)
const allItems = computed(() => {
	const newVideos = store.videos;
	const history = historyItems.value;

	// If we have enough new videos, use them
	if (newVideos.length >= 4) {
		return newVideos;
	}

	// Otherwise, fill with history videos
	const seen = new Set(newVideos.map(v => v.property.commercialId));
	const additionalHistory = history
		.filter(h => !seen.has(h.property.commercialId))
		.slice(0, 4 - newVideos.length)
		.map(item => ({
			property: item.property,
			videoUrl: item.videoUrl,
			thumbnailUrl: item.thumbnailUrl
		}));

	return [...newVideos, ...additionalHistory];
});

const MAX_VIDEOS = 15;

const limitedItems = computed(() => allItems.value.slice(0, MAX_VIDEOS));

const hasVideos = computed(() => limitedItems.value.length > 0);

// Show section if:
// - Store not loaded yet (show by default with skeleton)
// - Loading
// - Has videos (new or history)
// - All were watched (meaning there ARE videos in history)
const shouldShowSection = computed(
	() =>
		!store.isPreloaded || isLoading.value || hasVideos.value || store.allWatched
);

onMounted(async () => {
	const breakpoints = useBreakpoints(breakpointsTailwind);
	isMobile.value = breakpoints.smaller('md').value;

	watch(breakpoints.smaller('md'), val => {
		isMobile.value = val;
	});

	useIntersectionObserver(
		sectionRef,
		entries => {
			const entry = entries[0];
			if (entry) {
				isSectionVisible.value = entry.isIntersecting;
			}
		},
		{ threshold: 0.3 }
	);

	// Load both feed and history
	const promises: Promise<void>[] = [];

	if (!store.isPreloaded) {
		promises.push(store.preload());
	}

	if (!isHistoryLoaded.value) {
		promises.push(loadHistory());
	}

	await Promise.all(promises);
});

// StandardSlider ref for watching scroll position
const sliderRef = ref<{
	currentCarouselItemIndex: number;
	scrollTo: (index: number) => void;
} | null>(null);

const isLoadingMore = ref(false);

async function loadMore() {
	if (isLoadingMore.value || store.isLoading || !store.hasMore) return;
	if (limitedItems.value.length >= MAX_VIDEOS) return;

	isLoadingMore.value = true;
	await store.loadMore();
	isLoadingMore.value = false;
}

watch(
	() => sliderRef.value?.currentCarouselItemIndex,
	index => {
		if (index === undefined) return;

		activeSlideIndex.value = index;

		if (isLoadingMore.value || store.isLoading) return;
		if (limitedItems.value.length >= MAX_VIDEOS) return;

		const totalItems = limitedItems.value.length;
		const threshold = Math.max(0, totalItems - 3);

		if (index >= threshold && store.hasMore) {
			loadMore();
		}
	}
);

const route = useRoute();
const { trackCtaViewMoreClicked } = useEvents();
const ctaClickCount = ref(0);

const lastVideoState = useLastVideoState();

function handleVideoCardClick(index: number): void {
	const item = limitedItems.value[index];
	if (item) {
		lastVideoState.save(item.property.commercialId, index);
	}
	navigateToReels();
}

function handleCtaClick(): void {
	ctaClickCount.value += 1;
	trackCtaViewMoreClicked({
		[MixpanelProps.CTA_TEXT]: 'Explorar todos os Shorts',
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: 'shorts',
		[MixpanelProps.DESTINATION_URL]: '/shorts',
		[MixpanelProps.CLICK_COUNT]: ctaClickCount.value
	});
	navigateToReels();
}

const carouselItemClass =
	'basis-[94%] md:basis-[48%] lg:basis-[32%] xl:basis-[24%]';
</script>

<template>
	<SectionSkeleton v-if="shouldShowSection && isLoading" />

	<section
		v-else-if="shouldShowSection"
		ref="sectionRef"
		class="w-full bg-primary py-12 md:py-16"
	>
		<StandardSlider
			ref="sliderRef"
			:items="limitedItems"
			:is-loading="false"
			:carousel-item-class="carouselItemClass"
			:carousel-options="{ cyclic: false }"
			:limit-photos="false"
			section-name="featured videos"
			container-class="bg-transparent!"
			carousel-content-class="gap-5"
			selector-variant="white"
			selector-arrows-variant="default"
			:show-navigation-arrows="false"
		>
			<template #title>
				<div class="flex items-center gap-3">
					<IconMaterialSymbolsLightPlayCircle
						class="size-7 md:size-12 text-white"
					/>
					<h2
						class="font-matter-sq text-2xl font-normal leading-[1.5] tracking-tight text-white md:text-4xl"
					>
						Descubra imóveis em vídeo
					</h2>
				</div>
			</template>

			<template #items="{ item, index }">
				<VideoCard
					:property="item.property as FeaturedVideoProperty"
					:thumbnail-url="item.thumbnailUrl"
					:video-url="item.videoUrl"
					:index="index"
					:is-active="shouldAutoplayMobile && activeSlideIndex === index"
					@click="handleVideoCardClick"
				/>
			</template>

			<template #footer="{ totalSlides, scrollTo: footerScrollTo }">
				<div
					class="flex h-auto w-full flex-col-reverse items-start justify-between gap-6 md:flex-row lg:items-center"
				>
					<Button
						variant="primary-dark"
						size="responsive"
						class="text-base font-medium"
						@click="handleCtaClick"
					>
						<span>Explorar todos os Shorts</span>
						<IconMaterialSymbolsLightNorthEast
							class="size-5 xl:size-6 text-primary"
						/>
					</Button>

					<div
						v-if="totalSlides > 1"
						class="flex w-full items-center justify-end gap-3"
					>
						<div
							class="flex w-full flex-wrap justify-end gap-2 md:max-w-[420px] md:gap-3"
						>
							<CarouselItemSelector
								v-for="(_, index) in totalSlides"
								:key="`selector-${index}`"
								:active="activeSlideIndex === index"
								:delay="2000"
								size="default"
								variant="white"
								@click="footerScrollTo(index)"
							/>
						</div>
						<div class="flex max-w-[66px] gap-1 md:gap-2">
							<CarouselGenericAction
								variant="default"
								@click="footerScrollTo(activeSlideIndex - 1)"
							>
								<IconMaterialSymbolsLightChevronLeft
									width="32px"
									height="32px"
								/>
							</CarouselGenericAction>
							<CarouselGenericAction
								variant="default"
								@click="footerScrollTo(activeSlideIndex + 1)"
							>
								<IconMaterialSymbolsLightChevronRight
									width="32px"
									height="32px"
								/>
							</CarouselGenericAction>
						</div>
					</div>
				</div>
			</template>
		</StandardSlider>
	</section>
</template>
